/**
 * WithNav — authenticated layout shell.
 *
 * Renders `TitleBar`, `Navbar`, the page `<Outlet>`, and the slide-in
 * `PageModal` side-panels (dashboard, password).
 *
 * The watchlist is now managed entirely inside `MarketWatchPanel` (chart sidebar),
 * so no popup modal or custom event listener is needed here.
 */
import { useState, useCallback, useEffect } from "react";
import { Outlet } from "react-router-dom";
import { useSelector } from "react-redux";

import "./NavBar.scss";
import Navbar from "./Navbar";
import TitleBar from "@components/title-bar/TitleBar";
import PageModal from "./PageModal";
import Dashboard from "@components/dashboard/Dashboard";
import ChangePassword from "@components/change-password/ChangePassword";
import { getColorTheme } from "@service/local-storage-handler";
import AmibrokerWatchlistModal from "@components/chart/AmibrokerWatchlistModal";
import { isDesktop } from "@platform";
import { useSubscriptionExpiryReminder } from "../../hooks/useSubscriptionExpiryReminder";
import { connectWS } from "@service/websocket-handler";

export type ModalPage = "dashboard" | "password" | null;

const WithNav = () => {
  const [activeModal, setActiveModal] = useState<ModalPage>(null);
  const themeFromStore = useSelector(({ theme }) => theme.theme);
  const isAuthenticated = useSelector(({ auth }: any) => auth?.isAuthenticated);
  const connectionState = useSelector(
    ({ connectionStatus }: any) => connectionStatus?.state || "disconnected"
  );
  const colorTheme = themeFromStore || getColorTheme() || "dark";

  useSubscriptionExpiryReminder();

  const openModal = useCallback((page: ModalPage) => setActiveModal(page), []);
  const closeModal = useCallback(() => setActiveModal(null), []);

  // Open modals via custom events dispatched from GlobalChartToolbar
  useEffect(() => {
    const handleOpenDashboard = () => setActiveModal("dashboard");
    const handleOpenPassword = () => setActiveModal("password");
    window.addEventListener("open-dashboard-modal", handleOpenDashboard);
    window.addEventListener("open-password-modal", handleOpenPassword);
    return () => {
      window.removeEventListener("open-dashboard-modal", handleOpenDashboard);
      window.removeEventListener("open-password-modal", handleOpenPassword);
    };
  }, []);

  // Ensure live ticks resume after reload/navigation while already authenticated.
  useEffect(() => {
    if (!isAuthenticated) return;
    if (connectionState === "connected" || connectionState === "connecting") return;

    connectWS().catch(() => {
      // Reconnect path is already handled inside websocket-handler.
    });
  }, [isAuthenticated, connectionState]);

  return (
    <div className="app-layout">
      <TitleBar showNetworkStatus={true} />
      <div className="navbar-content">
        <Outlet />
      </div>

      {/* Page modals rendered over the chart */}
      <PageModal
        isOpen={activeModal === "dashboard"}
        onClose={closeModal}
        title="Dashboard"
        theme={colorTheme}
      >
        <Dashboard />
      </PageModal>

      <PageModal
        isOpen={activeModal === "password"}
        onClose={closeModal}
        title="Change Password"
        theme={colorTheme}
      >
        <ChangePassword />
      </PageModal>

      {/* AmiBroker watchlist modal — desktop only */}
      {isDesktop && <AmibrokerWatchlistModal />}
    </div>
  );
};

export default WithNav;
